import type { Track } from '@nyos/shared'

/**
 * Resolves the source-track list for an inbound interchange manifest.
 *
 * Option A (accepted 2026-10-07): Marietta has no dedicated receiving lead —
 * manifest cars arrive with no current_track_id, so the source is derived:
 *   1. manifest tracks (cars already carry a track) take precedence;
 *   2. otherwise every routable track stays selectable — the UI separately
 *      defaults the pick to the selected locomotive's current track, which is
 *      also what LOCOMOTIVE_NOT_ON_SOURCE_TRACK requires to pass.
 */
export type SourceTrackMode = 'manifest' | 'routable'

export interface SourceTrackResolution {
  tracks: Track[]
  mode: SourceTrackMode
}

export function resolveSourceTracks(
  availableTracks: Track[],
  manifestTrackIds: Set<string>
): SourceTrackResolution {
  const manifestTracks = availableTracks.filter(track => manifestTrackIds.has(track.id))
  if (manifestTracks.length > 0) {
    return { tracks: manifestTracks, mode: 'manifest' }
  }
  return { tracks: availableTracks, mode: 'routable' }
}

/** Track to default the source pick to — the selected locomotive's current track. */
export function resolveLocoSourceDefault(
  availableTracks: Track[],
  locomotiveId: string | null,
  locomotives: Array<{ id: string; kinematics?: { currentTrackId?: string } }>
): Track | null {
  if (!locomotiveId) return null
  const loco = locomotives.find(l => l.id === locomotiveId)
  const trackId = loco?.kinematics?.currentTrackId
  if (!trackId) return null
  return availableTracks.find(t => t.id === trackId) ?? null
}
